<template>
  <div class="my-orders-page">
    <site-header />
    <main class="my-orders-page__main">
      <div class="core-container">
        <h1 class="my-orders-page__title">我的订单</h1>

        <div v-if="loading && !orderList.length" class="my-orders-page__state">
          <div class="my-orders-page__spinner" />
          <p>加载中...</p>
        </div>

        <div v-else-if="!orderList.length" class="my-orders-page__empty">
          <p>暂无订单</p>
          <router-link to="/search" class="my-orders-page__link">去逛逛</router-link>
        </div>

        <div v-else class="my-orders-page__list">
          <article v-for="order in orderList" :key="order.id" class="order-item">
            <div class="order-item__header">
              <span>{{ order.order_no }}</span>
              <div class="order-item__status-wrap">
                <span :class="['order-item__status', `is-${order.status}`]">{{ order.status_text }}</span>
                <span v-if="order.refund_status_text" class="order-item__refund">{{ order.refund_status_text }}</span>
              </div>
            </div>
            <div class="order-item__body">
              <div class="order-item__image">
                <img v-if="order.cover_img" :src="order.cover_img" :alt="order.tit" />
                <span v-else>{{ order.tit ? order.tit[0] : '标' }}</span>
              </div>
              <div class="order-item__info">
                <h2>{{ order.tit }}</h2>
                <p>{{ order.classify }}类 · {{ order.reg_no }}</p>
                <p>{{ order.need_notary_text }}</p>
                <p class="order-item__price">总价 ¥{{ formatPrice(order.selling_price) }}</p>
                <p v-if="order.status === ORDER_STATUS.PENDING" class="order-item__stage">
                  {{ order.need_notary === 1 ? `待付定金 ¥${formatPrice(order.deposit_amount)}` : `待付全款 ¥${formatPrice(order.selling_price)}` }}
                </p>
                <p v-else-if="order.status === ORDER_STATUS.NOTARIZED" class="order-item__stage">
                  待付尾款 ¥{{ formatPrice(order.final_amount) }}
                </p>
              </div>
            </div>
            <div class="order-item__footer">
              <span>{{ order.created_at }}</span>
              <div class="order-item__actions">
                <button
                  v-if="order.status === ORDER_STATUS.PENDING"
                  type="button"
                  class="order-item__btn"
                  @click="cancelOrder(order)"
                >取消订单</button>
                <button
                  v-if="order.status === ORDER_STATUS.PENDING"
                  type="button"
                  class="order-item__btn is-primary"
                  :disabled="payingOrderId === order.id"
                  @click="goPay(order)"
                >去支付</button>
                <button
                  v-if="canApplyRefund(order)"
                  type="button"
                  class="order-item__btn"
                  @click="applyRefund(order)"
                >申请退款</button>
                <button
                  v-if="order.status === ORDER_STATUS.NOTARIZED"
                  type="button"
                  class="order-item__btn is-primary"
                  :disabled="payingOrderId === order.id"
                  @click="goPayFinal(order)"
                >支付尾款</button>
              </div>
            </div>
          </article>

          <button
            v-if="!finished"
            type="button"
            class="my-orders-page__more"
            :disabled="loading"
            @click="loadOrders"
          >{{ loading ? '加载中...' : '加载更多' }}</button>
        </div>
      </div>
    </main>
    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import { fetchOrderList, payOrder, updateOrderStatus, applyOrderRefund } from '@/api/order'
import { ORDER_PAGE_SIZE, ORDER_STATUS } from '@/constants/auth'
import { canApplyRefund } from '@/constants/orderRefund'

export default {
  name: 'MyOrdersPage',
  components: {
    SiteHeader,
    SiteFooter
  },
  data() {
    return {
      ORDER_STATUS,
      orderList: [],
      loading: false,
      finished: false,
      page: 1,
      payingOrderId: null
    }
  },
  created() {
    this.loadOrders()
    this.handlePayQuery()
  },
  methods: {
    canApplyRefund,
    handlePayQuery() {
      const query = this.$route.query
      if (query.pay_order_no && query.qr_url) {
        this.$store.commit('SHOW_PAYMENT', {
          qrUrl: decodeURIComponent(query.qr_url),
          orderNo: query.pay_order_no,
          orderStatus: parseInt(query.pay_status || ORDER_STATUS.PENDING, 10)
        })
      }
    },
    loadOrders() {
      if (this.loading || this.finished) return
      this.loading = true

      fetchOrderList({
        page: this.page,
        pageSize: ORDER_PAGE_SIZE
      })
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.finished = true
            return
          }
          const list = result.data.list || []
          this.orderList = this.orderList.concat(list)
          this.page += 1
          if (list.length < ORDER_PAGE_SIZE) {
            this.finished = true
          }
        })
        .catch(() => {
          this.finished = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    goPay(order) {
      const isDeposit = Number(order.need_notary) === 1
      const amount = isDeposit ? order.deposit_amount : order.selling_price
      const type = isDeposit ? 'deposit' : 'full'
      this.executePay(order.id, order.order_no, amount, type, order.status)
    },
    goPayFinal(order) {
      this.executePay(order.id, order.order_no, order.final_amount, 'final', order.status)
    },
    executePay(orderId, orderNo, amount, type, initialStatus) {
      this.payingOrderId = orderId
      payOrder({ order_no: orderNo, amount, type })
        .then(result => {
          if (result.status === 1 && result.data && result.data.qrCodeUrl) {
            const qrCodeUrl = result.data.qrCodeUrl
            const isWechat = /MicroMessenger/i.test(navigator.userAgent)
            if (isWechat) {
              window.location.href = qrCodeUrl
              return
            }
            this.$store.commit('SHOW_PAYMENT', {
              qrUrl: qrCodeUrl,
              orderNo,
              orderStatus: initialStatus,
              payAmount: amount
            })
            return
          }
          window.alert(result.desc || result.msg || '支付失败，请重试')
        })
        .catch(() => {
          window.alert('网络错误，请重试')
        })
        .finally(() => {
          this.payingOrderId = null
        })
    },
    cancelOrder(order) {
      if (!window.confirm('确定要取消该订单吗？')) return

      updateOrderStatus(order.id, ORDER_STATUS.CANCELLED)
        .then(result => {
          if (result.status === 1) {
            this.resetAndReload()
            return
          }
          window.alert(result.desc || result.msg || '取消失败')
        })
        .catch(() => {
          window.alert('网络错误，取消失败')
        })
    },
    applyRefund(order) {
      const reason = window.prompt('请填写退款原因，提交后需代卖方或平台审核')
      if (reason === null) return
      const text = reason.trim()
      if (!text) {
        window.alert('请填写退款原因')
        return
      }

      applyOrderRefund(order.id, text)
        .then(result => {
          if (result.status === 1) {
            window.alert(result.desc || result.msg || '退款申请已提交')
            this.resetAndReload()
            return
          }
          window.alert(result.desc || result.msg || '申请失败')
        })
        .catch(() => {
          window.alert('网络错误，请重试')
        })
    },
    resetAndReload() {
      this.orderList = []
      this.page = 1
      this.finished = false
      this.loadOrders()
    },
    formatPrice(price) {
      if (!price || price <= 0) return '0'
      return Math.round(price).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.my-orders-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.my-orders-page__main {
  padding: var(--space-lg) 0 var(--space-xl);
}

.my-orders-page__title {
  margin-bottom: var(--space-lg);
  font-size: var(--font-size-xl);
}

.my-orders-page__state,
.my-orders-page__empty {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-md);
  color: var(--color-text-muted);
}

.my-orders-page__spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-light);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: orders-spin 0.8s linear infinite;
}

.my-orders-page__link {
  color: var(--color-primary);
}

.my-orders-page__list {
  display: grid;
  gap: var(--space-lg);
}

.order-item {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
}

.order-item__header,
.order-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
  background: var(--color-bg-section);
  font-size: var(--font-size-sm);
}

.order-item__status-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.order-item__refund {
  font-size: var(--font-size-sm);
  color: #fa8c16;
}

.order-item__status.is-0 { color: var(--color-price); }
.order-item__status.is-4 { color: #16a34a; }
.order-item__status.is-5 { color: var(--color-text-muted); }

.order-item__body {
  display: flex;
  gap: var(--space-lg);
  padding: var(--space-lg);
}

.order-item__image {
  width: 96px;
  height: 96px;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-bg-main);
  display: flex;
  align-items: center;
  justify-content: center;
}

.order-item__image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.order-item__info h2 {
  font-size: var(--font-size-md);
  margin-bottom: var(--space-xs);
}

.order-item__info p {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.order-item__price,
.order-item__stage {
  margin-top: var(--space-sm);
  color: var(--color-price);
}

.order-item__actions {
  display: flex;
  gap: var(--space-sm);
}

.order-item__btn {
  padding: var(--space-xs) var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-bg-page);
  font-size: var(--font-size-sm);
}

.order-item__btn.is-primary {
  border-color: var(--color-price);
  color: var(--color-price);
}

.my-orders-page__more {
  justify-self: center;
  padding: var(--space-sm) var(--space-xl);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-bg-page);
}

@keyframes orders-spin {
  to { transform: rotate(360deg); }
}
</style>
